import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Expand, X } from 'lucide-react';

/**
 * ProductGallery — luxury image gallery.
 * - Thumbnail strip (vertical on desktop, horizontal on mobile)
 * - Hover-zoom magnifier on the main image (desktop)
 * - Fullscreen lightbox with prev/next + keyboard navigation
 * Token-driven, no hardcoded hex.
 */
export default function ProductGallery({ images = [], alt = '' }) {
  const cleanImages = images.filter(Boolean);
  const list = cleanImages.length ? cleanImages : [''];

  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const mainRef = useRef(null);

  // Reset to first image when the image set changes (e.g. variant switch)
  const imagesKey = cleanImages.join('|');
  useEffect(() => {
    setActiveIndex(0);
  }, [imagesKey]);

  const safeIndex = Math.min(activeIndex, list.length - 1);
  const current = list[safeIndex];

  const goPrev = () => setActiveIndex((i) => (i - 1 + list.length) % list.length);
  const goNext = () => setActiveIndex((i) => (i + 1) % list.length);

  const handleMouseMove = (e) => {
    const rect = mainRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ active: true, x, y });
  };

  // Keyboard navigation inside the lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen, list.length]);

  return (
    <div className="flex flex-col-reverse lg:flex-row gap-4" data-testid="product-gallery">
      {/* Thumbnails */}
      {list.length > 1 && (
        <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible scrollbar-hide">
          {list.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setActiveIndex(i)}
              className={`relative flex-shrink-0 w-16 h-20 md:w-20 md:h-24 rounded-xl overflow-hidden bg-secondary transition-all duration-hover ease-out ${
                i === safeIndex
                  ? 'ring-2 ring-bordeaux ring-offset-2 ring-offset-ivory'
                  : 'opacity-60 hover:opacity-100'
              }`}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Vedi immagine ${i + 1}`}
              aria-current={i === safeIndex}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Main image */}
      <div className="flex-1">
        <div
          ref={mainRef}
          onMouseEnter={() => setZoom((z) => ({ ...z, active: true }))}
          onMouseLeave={() => setZoom({ active: false, x: 50, y: 50 })}
          onMouseMove={handleMouseMove}
          onClick={() => setLightboxOpen(true)}
          className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-secondary cursor-zoom-in"
          data-testid="gallery-main"
        >
          <img
            key={current}
            src={current}
            alt={alt}
            className="w-full h-full object-cover transition-transform duration-300 ease-out animate-fade-in"
            style={
              zoom.active
                ? { transform: 'scale(1.9)', transformOrigin: `${zoom.x}% ${zoom.y}%` }
                : { transform: 'scale(1)' }
            }
          />

          {/* Expand hint */}
          <span className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ivory/90 backdrop-blur flex items-center justify-center text-ink opacity-0 group-hover:opacity-100 transition-opacity duration-hover pointer-events-none">
            <Expand className="w-4 h-4" />
          </span>

          {/* Arrows */}
          {list.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ivory/90 backdrop-blur flex items-center justify-center text-ink opacity-0 group-hover:opacity-100 hover:bg-ivory transition-all duration-hover"
                data-testid="gallery-prev"
                aria-label="Immagine precedente"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-ivory/90 backdrop-blur flex items-center justify-center text-ink opacity-0 group-hover:opacity-100 hover:bg-ivory transition-all duration-hover"
                data-testid="gallery-next"
                aria-label="Immagine successiva"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-5xl w-[95vw] bg-ink border-none p-0 overflow-hidden"
          data-testid="gallery-lightbox"
        >
          <DialogTitle className="sr-only">{alt || 'Immagine prodotto'}</DialogTitle>
          <DialogDescription className="sr-only">
            Visualizzazione a schermo intero della galleria immagini del prodotto.
          </DialogDescription>
          <div className="relative">
            <img
              src={current}
              alt={alt}
              className="w-full max-h-[82vh] object-contain bg-ink"
            />
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition-colors duration-hover"
              data-testid="lightbox-close"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>

            {list.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition-colors duration-hover"
                  data-testid="lightbox-prev"
                  aria-label="Immagine precedente"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-ivory/10 hover:bg-ivory/20 text-ivory flex items-center justify-center transition-colors duration-hover"
                  data-testid="lightbox-next"
                  aria-label="Immagine successiva"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {list.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveIndex(i)}
                      className={`w-2 h-2 rounded-full transition-colors duration-hover ${
                        i === safeIndex ? 'bg-ivory' : 'bg-ivory/30'
                      }`}
                      aria-label={`Vai all'immagine ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
